import {
  flush,
  identify,
  Identify,
  setUserId,
  track,
} from "@amplitude/analytics-browser";
import clevertap, { isCleverTapInitialized } from "../clevertapClient";

// --- Types ---

export interface UserProperties {
  Name?: string;
  Identity?: string; // User ID
  Phone?: string; // Standard CleverTap property
  Email?: string; // Standard CleverTap property
  Gender?: string; // M/F
  DOB?: Date | string; // Date of Birth
  Age?: number; // User's age
  "MSG-email"?: boolean;
  "MSG-push"?: boolean;
  "MSG-sms"?: boolean;
  "MSG-whatsapp"?: boolean;
  wallet_balance?: number;
  currency?: string;
  plan_tier?: string;
  subscription_status?: string;
  "Total Profiles"?: number;
  "Total Amount Recharged"?: number;
  "Recent Amount Recharged"?: number;
  no_of_chats_left?: number;
  call_minutes_left?: number;
  total_chats_done?: number;
  total_call_minutes_done?: number;
  age_bucket?: string;
  preferred_service?: string;
  Language?: string;
  // Astro Properties
  current_mahadasha?: string;
  current_antardasha?: string;
  birth_moon_sign?: string;
  birth_sun_sign?: string;
  birth_ascendant_sign?: string;
  [key: string]: any;
}

export interface PurchaseItem {
  itemId: string;
  itemName: string;
  price: number;
  quantity: number;
}

// --- Helper Functions ---

function getCleverTap() {
  try {
    if (clevertap && typeof clevertap?.event?.push === "function") {
      return clevertap;
    }
    if (typeof window !== "undefined" && (window as any).clevertap) {
      const winCleverTap = (window as any).clevertap;
      if (typeof winCleverTap.event?.push === "function") {
        return winCleverTap;
      }
    }
  } catch (err) {
    console.warn("[Analytics] Error accessing CleverTap:", err);
  }
  return null;
}

function getMetaPixel() {
  if (typeof window !== "undefined" && (window as any).fbq) {
    return (window as any).fbq;
  }
  return null;
}

function getGtag() {
  if (typeof window !== "undefined" && (window as any).gtag) {
    return (window as any).gtag;
  }
  return null;
}

/**
 * GA4 event names must start with a letter and contain only letters, numbers
 * and underscores (40 chars max) — names with spaces get dropped on ingest.
 * Our names are human-readable for Amplitude/CleverTap, so we convert for GA4
 * only: "Chat Message Sent" -> "Chat_Message_Sent".
 *
 * CASE IS PRESERVED, and that matters: GA4 event names are case-SENSITIVE, and
 * the app (analytics_service.dart) sanitizes with
 * `eventName.replaceAll(RegExp(r'[^a-zA-Z0-9_]'), '_')` — no lowercasing. This
 * used to lowercase, so the same user action arrived as TWO different events —
 * app `Profile_Creation_Submitted` vs web `profile_creation_submitted` — and
 * any report filtered on the app's name silently excluded every web user.
 * Keep this byte-identical to the app's rule.
 */
function toGa4EventName(name: string): string {
  // Per-character replacement, NOT collapsing runs, and NO trimming of
  // underscores — the app replaces each invalid char 1:1, so "x tab (app)"
  // must land as "x_tab__app_" on both platforms or the names diverge again.
  const cleaned = name.trim().replace(/[^a-zA-Z0-9_]/g, "_");
  // Safety the app lacks: GA4 rejects a name that doesn't start with a letter.
  const safe = /^[a-zA-Z]/.test(cleaned) ? cleaned : `evt_${cleaned}`;
  return safe.slice(0, 40);
}

// --- Main Analytics Functions ---

export const AnalyticsManager = {
  /**
   * Initialize Analytics (if specific client-side logic needed beyond imports)
   */
  init: () => {
    // Amplitude is initialized in index.tsx currently.
    // CleverTap is initialized in clevertapClient.ts
    console.log(
      "[AnalyticsManager] Initialized. Environment:",
      process.env.NODE_ENV,
    );
  },

  /**
   * Set the User ID across all platforms
   */
  setUserId: (userId: string | null) => {
    if (!userId) {
      setUserId(null); // Amplitude logout/reset
      return;
    }

    // Amplitude
    setUserId(userId);

    // GA4 — the app sets a Firebase user id, the website never did, so a person
    // who used both counted as two unrelated users and no web↔app journey could
    // be stitched. Same Supabase uuid on both platforms.
    try {
      const gtag = getGtag();
      if (gtag) gtag("set", { user_id: String(userId) });
    } catch (err) {
      console.error("[Analytics] ❌ GA4 setUserId failed:", err);
    }

    // CleverTap (onUserLogin)
    const ct = getCleverTap();
    if (ct && ct.onUserLogin) {
      // ✅ FIX: Wrapped in "Site" key
      const loginPayload = {
        Site: {
          Identity: String(userId), // Ensure string
        },
      };

      if (process.env.NODE_ENV === "development") {
        console.log(
          "[Analytics] Pushing to CleverTap onUserLogin:",
          loginPayload,
        );
      }

      try {
        ct.onUserLogin.push(loginPayload);
      } catch (err) {
        console.error("[Analytics] ❌ CleverTap onUserLogin failed:", err);
      }
    }
  },

  /**
   * Sync User Properties to all platforms
   * @param properties User properties to sync
   */
  setUserProperties: (properties: UserProperties) => {
    // console.log('[Analytics] setUserProperties called with:', properties); // Optional: Uncomment for very verbose logs

    // 1. Amplitude
    const identifyObj = new Identify();
    Object.keys(properties).forEach((key) => {
      const value = properties[key];
      if (value !== undefined && value !== null) {
        identifyObj.set(key, value);
      }
    });
    // Set 'source' property to identify web users
    identifyObj.set("source", "web_app");
    identify(identifyObj);

    // 2. CleverTap (Profile Push)
    const ct = getCleverTap();
    if (ct && ct.profile) {
      const ctProps: Record<string, any> = { source: "web_app" };

      Object.keys(properties).forEach((key) => {
        const value = properties[key];
        // 'Identity' is used for lookup, not update. Remove it to avoid "Incorrect format".
        if (key !== "Identity" && value !== undefined && value !== null) {
          // 3. Handle Dates: Convert date strings to Date objects
          if (
            (key === "DOB" || key === "dob" || key === "last_recharge_date") &&
            typeof value === "string"
          ) {
            const dateVal = new Date(value);
            if (!isNaN(dateVal.getTime())) {
              ctProps[key] = dateVal;
            } else {
              ctProps[key] = value;
            }
          } else {
            ctProps[key] = value;
          }
        }
      });

      // ✅ FIX: Wrapped in "Site" key for profile push as well
      const finalPayload = { Site: ctProps };

      // Explicitly log if we are tracking recharge amount
      if (properties["Recent Amount Recharged"]) {
        console.log(
          "[Analytics] Pushing Recent Amount Recharged:",
          properties["Recent Amount Recharged"],
          finalPayload,
        );
      }

      const isDev = process.env.NODE_ENV === "development";
      console.log(
        `[Analytics] Pushing to CleverTap profile (Environment: ${process.env.NODE_ENV}):`,
        finalPayload,
      );

      try {
        ct.profile.push(finalPayload);
      } catch (err) {
        console.error(
          "[Analytics] ❌ CleverTap profile push failed:",
          err,
          finalPayload,
        );
      }
    }
  },

  /**
   * Increment a User Property (Atomic Add)
   * Useful for lifetime totals like "Total Amount Recharged"
   */
  incrementUserProperties: (properties: Record<string, number>) => {
    console.log("[Analytics] incrementUserProperties called with:", properties);

    // 1. Amplitude
    const identifyObj = new Identify();
    Object.keys(properties).forEach((key) => {
      const value = properties[key];
      if (typeof value === "number") {
        (identifyObj as any).add(key, value);
      } else {
        console.warn(
          `[Analytics] incrementUserProperties detected non-number for key "${key}":`,
          value,
          typeof value,
        );
      }
    });
    identify(identifyObj);

    // 2. CleverTap (Profile Add - $add)
    const ct = getCleverTap();
    if (ct && ct.profile) {
      const ctProps: Record<string, any> = {};
      Object.keys(properties).forEach((key) => {
        const value = properties[key];
        if (typeof value === "number") {
          ctProps[key] = { $add: value };
        }
      });

      // Wrapped in "Site" key
      const finalPayload = { Site: { ...ctProps, source: "web_app" } };

      console.log(
        "[Analytics] Incrementing CleverTap profile (Payload):",
        finalPayload,
      );

      try {
        ct.profile.push(finalPayload);
      } catch (err) {
        console.error(
          "[Analytics] ❌ CleverTap profile increment failed:",
          err,
        );
      }
    } else {
      console.warn(
        "[Analytics] CleverTap object or profile not found during incrementUserProperties",
      );
    }
  },

  /**
   * Track a standard event
   */
  trackEvent: (eventName: string, props?: Record<string, any>) => {
    const eventProps = { ...props, source: "web_app" };

    // 1. Amplitude
    try {
      track(eventName, eventProps);
    } catch (err) {
      if (process.env.NODE_ENV === "development") {
        console.warn("[Analytics] Amplitude tracking failed:", err);
      }
    }

    // 2. CleverTap
    try {
      if (!isCleverTapInitialized) {
        if (process.env.NODE_ENV === "development") {
          console.warn("[Analytics] ⚠️ CleverTap not fully initialized yet.");
        }
      }

      const ct = getCleverTap();
      if (ct && ct.event) {
        ct.event.push(eventName, eventProps);
        if (process.env.NODE_ENV === "development") {
          console.log("[Analytics] ✅ Event tracked:", eventName, eventProps);
        }
      }
    } catch (err) {
      console.error("[Analytics] ❌ CleverTap tracking failed:", err);
    }

    // 3. Meta Pixel (All Events)
    try {
      const fbq = getMetaPixel();
      if (fbq) {
        // Track as a Custom Event in Meta
        fbq("trackCustom", eventName, eventProps);
        if (process.env.NODE_ENV === "development") {
          console.log(
            "[Analytics] 🟦 Meta Pixel event tracked:",
            eventName,
            eventProps,
          );
        }
      }
    } catch (err) {
      console.error("[Analytics] ❌ Meta Pixel tracking failed:", err);
    }

    // 4. Google Analytics 4 (All Events)
    try {
      const gtag = getGtag();
      if (gtag) {
        const ga4Name = toGa4EventName(eventName);

        if (process.env.NODE_ENV === "development") {
          gtag("event", ga4Name, { ...eventProps, debug_mode: true });
          console.log("[Analytics] 🟧 GA4 event tracked:", ga4Name, eventProps);
        } else {
          gtag("event", ga4Name, eventProps);
        }
      }
    } catch (err) {
      console.error("[Analytics] ❌ GA4 tracking failed:", err);
    }
  },

  /**
   * Log a purchase event
   */
  logPurchase: (
    transactionId: string,
    value: number,
    currency: string,
    items?: PurchaseItem[],
    /** Amplitude-only properties (e.g. gateway, late), kept off the pixels. */
    extra?: Record<string, string | number | boolean>,
  ) => {
    const props = {
      transaction_id: transactionId,
      value: value,
      currency: currency,
      item_count: items?.length ?? 0,
      items_summary: items
        ?.map((e) => `${e.itemName} (${e.itemId})`)
        .join(", "),
      source: "web_app",
    };

    // 1. Amplitude
    track("purchase", { ...props, ...extra });

    // 2. CleverTap (Charged Event)
    const ct = getCleverTap();
    if (ct && ct.event) {
      const chargedDetails = {
        Amount: value,
        "Payment Mode": "Prepaid",
        "Charged ID": transactionId,
        Currency: currency,
        ...props,
      };

      const ctItems =
        items?.map((item) => ({
          Item: item.itemName,
          Price: item.price,
          Quantity: item.quantity,
          id: item.itemId,
        })) || [];

      ct.event.push("Charged", chargedDetails, ctItems);

      if (process.env.NODE_ENV === "development") {
        console.log("[Analytics] 💰 Purchase tracked:", chargedDetails);
      }
    }

    // 3. Meta Pixel (Purchase Event)
    try {
      const fbq = getMetaPixel();
      if (fbq) {
        const { value: pValue, currency: pCurrency, ...otherProps } = props;

        // Meta Pixel standard Purchase events require value in major currency units (e.g. 10.00 USD, not 1000 cents) across all currencies.
        // Since our platform consistently tracks in minor units (Paise/Cents), we universally divide by 100 for Meta.
        const metaValue = value / 100;

        fbq(
          "track",
          "Purchase",
          {
            value: metaValue,
            currency: currency,
            content_name: items?.map((e) => e.itemName).join(", ") || "Recharge",
            content_ids: items?.map((e) => e.itemId) || [],
            content_type: "product",
            num_items: items?.length ?? 1,
            ...otherProps,
          },
          // Meta drops a repeat with the same eventID, so one order can never
          // count twice (and server-side events can dedupe against it later).
          { eventID: transactionId },
        );
        if (process.env.NODE_ENV === "development") {
          console.log(
            "[Analytics] 🟦 Meta Pixel Purchase tracked:",
            metaValue,
            currency,
          );
        }
      }
    } catch (err) {
      console.error("[Analytics] ❌ Meta Pixel Purchase tracking failed:", err);
    }

    // 4. Google Analytics 4 (Purchase Event)
    try {
      const gtag = getGtag();
      if (gtag) {
        // GA4 standard Purchase events also require standard currency units (e.g. 10.00 USD).
        // Reuse the same logic as Meta Pixel.
        const ga4Value = value / 100;

        if (process.env.NODE_ENV === "development") {
          gtag("event", "purchase", {
            transaction_id: transactionId,
            value: ga4Value,
            currency: currency,
            items: items?.map((e) => ({
              item_id: e.itemId,
              item_name: e.itemName,
              price: e.price / 100,
              quantity: e.quantity,
            })),
          });
          console.log(
            "[Analytics] 🟧 GA4 Purchase tracked:",
            ga4Value,
            currency,
          );
        } else {
          gtag("event", "purchase", {
            transaction_id: transactionId,
            value: ga4Value,
            currency: currency,
            items: items?.map((e) => ({
              item_id: e.itemId,
              item_name: e.itemName,
              price: e.price / 100, // GA4 item price also should be standard unit
              quantity: e.quantity,
            })),
          });
        }
      }
    } catch (err) {
      console.error("[Analytics] ❌ GA4 Purchase tracking failed:", err);
    }
  },
};

export const trackEvent = AnalyticsManager.trackEvent;

/** Send Amplitude's queue now. It flushes every 10s on its own, so an event
 *  fired just before a full-page navigation would otherwise be left waiting. */
export const flushAnalytics = async (): Promise<void> => {
  await flush().promise;
};
